import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@core/auth/hooks/useAuth';
import { getModuleInfoList, getModuleByPath } from '@core/router/moduleRegistry';
import type { ModuleInfo } from '@core/router/types';

/**
 * ModuleSelector Component
 *
 * Dropdown selector in the sidebar header that allows users to switch
 * between modules (Fleet, Users, etc.) based on their permissions.
 *
 * Features:
 * - Permission-based module filtering
 * - Visual indicator of current module
 * - Smooth navigation on module change
 */
export const ModuleSelector = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Get all registered modules
  const allModules = getModuleInfoList();

  // Filter modules based on user permissions
  const accessibleModules = useMemo(() => {
    return allModules.filter((module) => {
      // If no permission required, module is accessible
      if (!module.accessPermission) return true;

      // Check if user has any permission that starts with the module prefix
      // e.g., "Fleet." for fleet module
      const modulePrefix = module.accessPermission.split('.')[0] + '.';
      return permissions.some((p) => p.startsWith(modulePrefix));
    });
  }, [allModules, permissions]);

  // Determine current module from URL
  const currentModule = useMemo(() => {
    const module = getModuleByPath(location.pathname);
    if (module?.moduleInfo) {
      return module.moduleInfo;
    }
    // Default to first accessible module or null
    return accessibleModules.length > 0 ? accessibleModules[0] : null;
  }, [location.pathname, accessibleModules]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleModuleSelect = (module: ModuleInfo) => {
    handleClose();
    // Navigate to the module's base path
    navigate(module.basePath);
  };

  // Don't render if no modules are accessible
  if (accessibleModules.length === 0) {
    return null;
  }

  // Single module - render as static display with border
  if (accessibleModules.length === 1 && currentModule) {
    return (
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          onClick={() => navigate(currentModule.basePath)}
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'primary.main',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ color: 'primary.main' }}
          >
            {t(currentModule.labelKey)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Button
        id="module-selector-button"
        aria-controls={open ? 'module-selector-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        fullWidth
        disableRipple
        sx={{
          justifyContent: 'flex-start',
          px: 2,
          py: 1.25,
          borderRadius: 1,
          textTransform: 'none',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'primary.main',
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'primary.dark',
          },
          '&:focus': {
            outline: 'none',
          },
          '&.Mui-focusVisible': {
            outline: 'none',
            boxShadow: 'none',
          },
        }}
      >
        {currentModule && (
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ flexGrow: 1, textAlign: 'left', color: 'primary.main' }}
          >
            {t(currentModule.labelKey)}
          </Typography>
        )}
        <KeyboardArrowDownIcon
          sx={{
            ml: 'auto',
            color: 'primary.main',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </Button>

      <Menu
        id="module-selector-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'module-selector-button',
          },
          paper: {
            sx: {
              width: anchorEl?.clientWidth,
              minWidth: 200,
              mt: 0.5,
            },
          },
        }}
      >
        {accessibleModules.map((module) => {
          const isSelected = currentModule?.id === module.id;
          return (
            <MenuItem
              key={module.id}
              onClick={() => handleModuleSelect(module)}
              selected={isSelected}
              sx={{
                py: 1.5,
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  '& .MuiSvgIcon-root': {
                    color: isSelected ? 'primary.main' : 'text.secondary',
                  },
                }}
              >
                {module.icon}
              </ListItemIcon>
              <ListItemText
                primary={t(module.labelKey)}
                slotProps={{
                  primary: {
                    sx: {
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? 'primary.main' : 'text.primary',
                    },
                  },
                }}
              />
              {isSelected && (
                <CheckIcon
                  sx={{
                    ml: 1,
                    color: 'primary.main',
                    fontSize: 20,
                  }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};
